/**
 * Service Request Pipeline Service
 *
 * Handles all business logic for the Service Request Pipeline:
 * - Creating and managing customer-initiated service change requests
 * - Stage transitions (with skip support — stages 03/04 are optional)
 * - Activity log tracking
 * - Optional "Send to Billing" exit action
 *
 * This pipeline is lighter than faults — no SLA, no deployment, no severity.
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  arrayUnion,
  deleteField,
} from 'firebase/firestore';
import { db } from '../firebase';
import { stripUndefinedDeep } from '@/utils/firestore';
import { getTenantIdSync, tenantIdReadUnion } from '@/lib/tenant';
import { createTicketWithNumber } from './ticket-number.service';
import { createDirectMentions } from './mention.service';
import { MentionEntityType } from '../types/mention.types';
import type {
  ServiceRequest,
  ServiceRequestStage,
  ServiceRequestTypeKey,
  ServiceRequestPriority,
  ServiceRequestIntakeMethod,
  ServiceRequestResolution,
  ServiceRequestActivity,
  ServiceRequestLineItem,
  ServiceRequestLineItemCategory,
  ServiceRequestVariation,
  ServiceRequestCurrentSnapshot,
  ServiceRequestStageOverride,
  ServiceRequestClosure,
  ServiceRequestClosurePart,
} from '../types/service-request.types';
import {
  SERVICE_REQUEST_CLOSURE_PARTS,
  getInitialStage,
  getCompletedStage,
  computeLineItemTotals,
  SERVICE_REQUEST_STAGE_LABELS,
} from '../types/service-request.types';
import type { ClientUpdate } from '../types/fault.types';
import {
  buildBillingCaptureUpdate,
  type BillingCaptureInput,
} from '@/services/billing-capture';
import { getCheckpointsForNewJob } from './checklist-template.service';
import { geocodeAddress } from './geocode.service';
import { JOB_FALLBACK_COORDS } from '../data/map-config';
import { composeSiteAddress } from '@/utils/address';

const COLLECTION = 'service_requests';

function getTenantId(): string {
  return getTenantIdSync();
}

function makeActivity(
  stage: ServiceRequestStage,
  author: string,
  action: string,
  details: string,
  kind?: ServiceRequestActivity['kind']
): ServiceRequestActivity {
  const entry: ServiceRequestActivity = {
    id: crypto.randomUUID(),
    timestamp: new Date().toISOString(),
    stage,
    author,
    action,
    details,
  };
  if (kind) entry.kind = kind;
  return entry;
}

/**
 * Strip fields with `undefined` values before writing to Firestore (which
 * rejects them). Deeply walks arrays and plain objects.
 */
function stripUndefined<T>(value: T): T {
  if (Array.isArray(value)) {
    return (value as unknown as unknown[]).map((v) => stripUndefined(v)) as unknown as T;
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (v === undefined) continue;
      out[k] = stripUndefined(v);
    }
    return out as T;
  }
  return value;
}

/**
 * Create a new service request (Stage 01-Intake)
 */
export async function createServiceRequest(data: {
  customerId: string;
  customerName: string;
  serviceId?: string;
  serviceType?: string;
  servicePlanName?: string;
  requestType: ServiceRequestTypeKey; // the active trade pack's request-type key
  title: string;
  description: string;
  priority: ServiceRequestPriority;
  intakeMethod: ServiceRequestIntakeMethod;
  intakeBy: string;
  intakeByName: string;
  /** Relocation details (requestType === 'relocating') */
  relocating?: ServiceRequest['relocating'];
  /** Account-closure details (requestType === 'account_closure') */
  closure?: ServiceRequestClosure;
}): Promise<string> {
  if (!data.customerId) {
    throw new Error('A valid customerId is required to create a service request');
  }
  if (!data.customerName?.trim()) {
    throw new Error('Customer name is required to create a service request');
  }
  if (!data.title?.trim()) {
    throw new Error('Title is required to create a service request');
  }

  const now = new Date().toISOString();
  const initialStage = getInitialStage(data.requestType);

  const initialActivity: ServiceRequestActivity = makeActivity(
    initialStage,
    data.intakeByName || data.intakeBy,
    'Request created',
    `${data.intakeMethod.charAt(0).toUpperCase() + data.intakeMethod.slice(1)} intake — ${data.description.slice(0, 200)}`
  );

  const request: Omit<ServiceRequest, 'id'> = {
    tenantId: getTenantId(),
    customerId: data.customerId,
    customerName: data.customerName,
    serviceId: data.serviceId,
    serviceType: data.serviceType,
    servicePlanName: data.servicePlanName,
    requestType: data.requestType,
    title: data.title.trim(),
    description: data.description.trim(),
    priority: data.priority,
    stage: initialStage,
    intakeMethod: data.intakeMethod,
    intakeDate: now,
    intakeBy: data.intakeBy,
    intakeByName: data.intakeByName,
    ...(data.relocating ? { relocating: data.relocating } : {}),
    ...(data.closure ? { closure: stripUndefinedDeep(data.closure) } : {}),
    activityLog: [initialActivity],
    customerUpdates: [],
    linkedJobIds: [],
    archived: false,
    createdAt: now,
    updatedAt: now,
  };

  // Same transaction reserves the SR-NNNN ticket number and writes the doc.
  const { id } = await createTicketWithNumber('service_request', request.tenantId, request);
  return id;
}

/**
 * Generic update (useful for simple field edits — priority, title, description, assignment)
 */
export async function updateServiceRequest(
  requestId: string,
  updates: Partial<ServiceRequest>
): Promise<void> {
  const sanitized = Object.fromEntries(
    Object.entries(updates).filter(([, v]) => v !== undefined)
  );
  await updateDoc(doc(db, COLLECTION, requestId), {
    ...sanitized,
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Write a pre-built billing field map (from `buildBillingCaptureUpdate`).
 *
 * Separate from `updateServiceRequest` because those fields carry Firestore
 * `FieldValue` sentinels (`deleteField()`), which `Partial<ServiceRequest>`
 * can't express and the undefined-filter above would be wrong to touch.
 * TWIN: FaultBillingCard writes the same map inline against `faults`.
 */
export async function updateServiceRequestBilling(
  requestId: string,
  fields: Record<string, unknown>
): Promise<void> {
  await updateDoc(doc(db, COLLECTION, requestId), {
    ...fields,
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Advance to any stage — supports skipping (e.g. Intake → Awaiting Customer)
 */
export async function advanceStage(
  requestId: string,
  targetStage: ServiceRequestStage,
  advancedBy: string,
  notes?: string
): Promise<void> {
  const reqRef = doc(db, COLLECTION, requestId);
  const snap = await getDoc(reqRef);
  if (!snap.exists()) throw new Error('Service request not found');

  const current = snap.data() as ServiceRequest;
  const now = new Date().toISOString();

  const entry = makeActivity(
    targetStage,
    advancedBy,
    `Moved to ${targetStage}`,
    notes || `Advanced from ${current.stage} to ${targetStage}`
  );

  await updateDoc(reqRef, {
    stage: targetStage,
    activityLog: [...(current.activityLog || []), entry],
    updatedAt: now,
  });
}

/**
 * Add a freeform internal note to the activity log.
 *
 * @deprecated Writes only to `ServiceRequest.activityLog[]`, which the
 * scheduler side panel + billing notes panel never read. Use the unified
 * notes collection instead so the note flows everywhere:
 *
 *   import { createNote } from '@/services/notes.service';
 *   await createNote({ entityType: 'service_request', entityId, source, ... });
 *
 * All in-app callers were migrated in V107.53.x. Kept only so historical
 * activityLog notes still display (the timeline renders them read-only).
 * Do not add new callers.
 */
export async function addInternalNote(
  requestId: string,
  author: string,
  note: string
): Promise<string> {
  const reqRef = doc(db, COLLECTION, requestId);
  const snap = await getDoc(reqRef);
  if (!snap.exists()) throw new Error('Service request not found');

  const current = snap.data() as ServiceRequest;
  const entry = makeActivity(current.stage, author, 'Internal note', note);

  await updateDoc(reqRef, {
    activityLog: [...(current.activityLog || []), entry],
    updatedAt: new Date().toISOString(),
  });

  return entry.id;
}

/**
 * Log a team tag to the activity log.
 *
 * The mention notifications + optional tech_requests row are created by the
 * caller — this helper only records the activity so the timeline shows who
 * was pulled in and why.
 */
export async function logTeamTagActivity(
  requestId: string,
  author: string,
  taggedNames: string[],
  note: string | undefined,
  sentToTechInbox: boolean
): Promise<string | undefined> {
  if (taggedNames.length === 0) return undefined;

  const reqRef = doc(db, COLLECTION, requestId);
  const snap = await getDoc(reqRef);
  if (!snap.exists()) throw new Error('Service request not found');

  const current = snap.data() as ServiceRequest;
  const names = taggedNames.join(', ');
  const action = sentToTechInbox
    ? `Tagged ${names} — tech request sent to inbox`
    : `Tagged ${names}`;
  const details = note?.trim() || '—';
  const entry = makeActivity(current.stage, author, action, details);

  await updateDoc(reqRef, {
    activityLog: [...(current.activityLog || []), entry],
    updatedAt: new Date().toISOString(),
  });

  return entry.id;
}

// ---------------------------------------------------------------------------
// Account closure — three owned parts (billing / provisioning / customer
// service), each ticked off separately. Writes use dotted paths into
// `closure.parts.<part>` and arrayUnion for the activity entry, so two people
// ticking different parts at once can't overwrite each other.
// ---------------------------------------------------------------------------

export interface ClosureActor {
  id: string;
  name: string;
}

export interface ClosureAlertRecipient {
  userId: string;
  userName: string;
  /** The parts this person owns, in checklist order. */
  parts: ServiceRequestClosurePart[];
}

/** Context the closure alert text and deep link are built from. */
export interface ClosureAlertContext {
  requestId: string;
  customerName: string;
  title: string;
  tenantId: string;
}

function closurePartLabel(part: ServiceRequestClosurePart): string {
  return SERVICE_REQUEST_CLOSURE_PARTS.find((p) => p.key === part)?.label ?? part;
}

/**
 * Who to alert for a set of closure parts: one entry per assignee (a person
 * owning two parts gets one alert naming both), in checklist order. The
 * creator is left out — they don't need telling about work they just assigned.
 */
export function closureAlertRecipients(
  parts: ServiceRequestClosure['parts'],
  creatorId: string,
): ClosureAlertRecipient[] {
  const byUser = new Map<string, ClosureAlertRecipient>();
  for (const { key } of SERVICE_REQUEST_CLOSURE_PARTS) {
    const part = parts[key];
    if (!part?.assigneeId || part.assigneeId === creatorId) continue;
    const existing = byUser.get(part.assigneeId);
    if (existing) {
      existing.parts.push(key);
    } else {
      byUser.set(part.assigneeId, {
        userId: part.assigneeId,
        userName: part.assigneeName || 'Teammate',
        parts: [key],
      });
    }
  }
  return [...byUser.values()];
}

/**
 * Send one bell + push alert per recipient (direct mentions — see
 * task-notify.ts for why not `createMentions`). Throws on failure; the caller
 * decides how loud to be about it.
 */
export async function sendClosureAlerts(
  ctx: ClosureAlertContext,
  recipients: ClosureAlertRecipient[],
  actor: ClosureActor,
  sourceNoteId?: string,
): Promise<void> {
  for (const r of recipients) {
    const partLabels = r.parts.map(closurePartLabel).join(' + ');
    await createDirectMentions({
      recipients: [{ userId: r.userId, userName: r.userName }],
      noteContent: `${actor.name} needs you on an account closure: ${partLabels} — ${ctx.customerName}`,
      entityType: MentionEntityType.SERVICE_REQUEST,
      entityId: ctx.requestId,
      entityTitle: `Service Request: ${ctx.customerName} — ${ctx.title}`,
      createdBy: actor.id,
      createdByName: actor.name,
      tenantId: ctx.tenantId,
      linkPath: `/home/service-requests?openId=${ctx.requestId}`,
      sourceNoteId,
    });
  }
}

/**
 * Alert every closure owner on a freshly created request and record who was
 * alerted on the timeline. Returns how many people were alerted.
 */
export async function notifyClosureOwners(
  ctx: ClosureAlertContext,
  stage: ServiceRequestStage,
  parts: ServiceRequestClosure['parts'],
  actor: ClosureActor,
): Promise<number> {
  const recipients = closureAlertRecipients(parts, actor.id);
  if (recipients.length === 0) return 0;

  const details = SERVICE_REQUEST_CLOSURE_PARTS
    .filter(({ key }) => recipients.some((r) => r.parts.includes(key)))
    .map(({ key, label }) => `${label}: ${parts[key]?.assigneeName || 'Teammate'}`)
    .join(' · ');
  const entry = makeActivity(stage, actor.name, 'Closure owners alerted', details, 'tag');
  await updateDoc(doc(db, COLLECTION, ctx.requestId), {
    activityLog: arrayUnion(entry),
    updatedAt: new Date().toISOString(),
  });

  await sendClosureAlerts(ctx, recipients, actor, entry.id);
  return recipients.length;
}

/** Tick a closure part done (records who + when) or untick it. */
export async function setClosurePartDone(
  requestId: string,
  stage: ServiceRequestStage,
  part: ServiceRequestClosurePart,
  done: boolean,
  actor: ClosureActor,
): Promise<void> {
  const now = new Date().toISOString();
  const label = closurePartLabel(part);
  const path = `closure.parts.${part}`;
  const entry = makeActivity(
    stage,
    actor.name,
    done ? `${label} closed` : `${label} reopened`,
    done ? `${label} marked done by ${actor.name}` : `${label} marked not done by ${actor.name}`,
  );

  await updateDoc(doc(db, COLLECTION, requestId), {
    [`${path}.doneAt`]: done ? now : deleteField(),
    [`${path}.doneById`]: done ? actor.id : deleteField(),
    [`${path}.doneByName`]: done ? actor.name : deleteField(),
    activityLog: arrayUnion(entry),
    updatedAt: now,
  });
}

/**
 * Set (or clear, with `owner: null`) who owns a closure part. Returns the
 * activity entry id so the caller can point the owner's alert at it.
 */
export async function setClosurePartOwner(
  requestId: string,
  stage: ServiceRequestStage,
  part: ServiceRequestClosurePart,
  owner: { id: string; name: string } | null,
  actor: ClosureActor,
): Promise<string> {
  const now = new Date().toISOString();
  const label = closurePartLabel(part);
  const path = `closure.parts.${part}`;
  const entry = makeActivity(
    stage,
    actor.name,
    owner ? `${label} owner set` : `${label} owner cleared`,
    owner ? `${label} now owned by ${owner.name}` : `${label} has no owner`,
    'tag',
  );

  await updateDoc(doc(db, COLLECTION, requestId), {
    [`${path}.assigneeId`]: owner ? owner.id : deleteField(),
    [`${path}.assigneeName`]: owner ? owner.name : deleteField(),
    activityLog: arrayUnion(entry),
    updatedAt: now,
  });
  return entry.id;
}

/**
 * Log an outbound customer communication (phone/sms/email)
 */
export async function addCustomerUpdate(
  requestId: string,
  method: 'phone' | 'sms' | 'email',
  content: string,
  author: string,
  nextUpdateDue?: string
): Promise<void> {
  const reqRef = doc(db, COLLECTION, requestId);
  const snap = await getDoc(reqRef);
  if (!snap.exists()) throw new Error('Service request not found');

  const current = snap.data() as ServiceRequest;
  const now = new Date().toISOString();

  const update: ClientUpdate = {
    id: crypto.randomUUID(),
    timestamp: now,
    author,
    method,
    content,
    nextUpdateDue,
  };

  const sanitizedUpdate = Object.fromEntries(
    Object.entries(update).filter(([, v]) => v !== undefined)
  ) as unknown as ClientUpdate;

  const activity = makeActivity(
    current.stage,
    author,
    `Contacted customer via ${method}`,
    content.length > 200 ? `${content.slice(0, 200)}…` : content
  );

  await updateDoc(reqRef, {
    customerUpdates: [...(current.customerUpdates || []), sanitizedUpdate],
    activityLog: [...(current.activityLog || []), activity],
    updatedAt: now,
  });
}

/**
 * Mark a request as completed. Does NOT automatically send to billing —
 * caller should invoke sendServiceRequestToBilling() separately if needed.
 */
/**
 * Hard-delete a service request — for records raised in error / duplicates
 * the office just wants gone, with no completion ceremony. Linked entities
 * (fault / deal / jobs) keep their own records; only their pointer to this
 * request dangles, which readers already tolerate.
 */
export async function deleteServiceRequest(requestId: string): Promise<void> {
  await deleteDoc(doc(db, 'service_requests', requestId));
  console.log('🗑️ Service request deleted:', requestId);
}

export async function completeServiceRequest(
  requestId: string,
  completedBy: string,
  completedByName: string,
  opts: {
    resolutionType?: ServiceRequestResolution;
    completionNotes?: string;
  } = {}
): Promise<void> {
  const reqRef = doc(db, COLLECTION, requestId);
  const snap = await getDoc(reqRef);
  if (!snap.exists()) throw new Error('Service request not found');

  const current = snap.data() as ServiceRequest;
  const now = new Date().toISOString();

  const resolution = opts.resolutionType || 'completed';
  const completedStage = getCompletedStage(current.requestType);
  const entry = makeActivity(
    completedStage,
    completedByName || completedBy,
    'Marked as completed',
    opts.completionNotes || `Resolution: ${resolution}`
  );

  await updateDoc(reqRef, {
    stage: completedStage,
    completedAt: now,
    completedBy,
    completedByName,
    completionNotes: opts.completionNotes || '',
    resolutionType: resolution,
    archived: true,
    archivedAt: now,
    activityLog: [...(current.activityLog || []), entry],
    updatedAt: now,
  });
}

/**
 * Re-open an archived/completed service request (unarchive, push back to In Progress)
 */
export async function reopenServiceRequest(
  requestId: string,
  reopenedBy: string,
  reason?: string
): Promise<void> {
  const reqRef = doc(db, COLLECTION, requestId);
  const snap = await getDoc(reqRef);
  if (!snap.exists()) throw new Error('Service request not found');

  const current = snap.data() as ServiceRequest;
  const now = new Date().toISOString();

  // Re-open to the first "working" stage in the pipeline (02 for standard, NBN-02 for NBN)
  const reopenStage: ServiceRequestStage =
    current.requestType === 'nbn_upgrade' ? 'NBN-02-Churned' : '02-In-Progress';

  const entry = makeActivity(
    reopenStage,
    reopenedBy,
    'Reopened',
    reason || 'Request reopened after completion'
  );

  await updateDoc(reqRef, {
    stage: reopenStage,
    archived: false,
    archivedAt: null,
    activityLog: [...(current.activityLog || []), entry],
    updatedAt: now,
  });
}

/**
 * Exit action: flag this service request for billing.
 * Shows up on /home/billing alongside deals + faults.
 *
 * `detail` carries the itemised capture from BillingCaptureModal — line items,
 * tax mode, and the tax/total the office confirmed. Supplying it REPLACES any
 * previous capture (see buildBillingCaptureUpdate); omitting it entirely is
 * the legacy flag-only path and leaves the billing fields alone.
 *
 * Calling it again on a request that was already sent is an EDIT: the
 * timeline reads "Billing updated" and the original sentToBillingAt/By/ByName
 * stay put. Callers only offer this while the request is still `to_bill`.
 *
 * TWIN: sendFaultToBilling in fault.service.ts. Both go through
 * buildBillingCaptureUpdate — change money behaviour there, not here.
 */
export async function sendServiceRequestToBilling(
  requestId: string,
  sentBy: string,
  sentByName: string,
  detail?: BillingCaptureInput,
  notes?: string
): Promise<void> {
  const reqRef = doc(db, COLLECTION, requestId);
  const snap = await getDoc(reqRef);
  if (!snap.exists()) throw new Error('Service request not found');

  const current = snap.data() as ServiceRequest;
  const now = new Date().toISOString();

  const capture = detail ? buildBillingCaptureUpdate(detail) : null;
  // Already sent → this is an edit of the lines ("Edit lines" on the Ready
  // to bill card). Log it as an update and keep the original sent stamp.
  const isEdit = !!current.sentToBilling;

  const entry = makeActivity(
    current.stage,
    sentByName || sentBy,
    isEdit ? 'Billing updated' : 'Sent to Billing',
    capture?.summary || notes || 'Flagged for billing'
  );

  const updates: Record<string, unknown> = {
    sentToBilling: true,
    ...(isEdit
      ? {}
      : {
          sentToBillingAt: now,
          sentToBillingBy: sentBy,
          sentToBillingByName: sentByName,
        }),
    billingStatus: 'to_bill',
    activityLog: [...(current.activityLog || []), entry],
    updatedAt: now,
    ...(capture?.fields ?? {}),
  };

  await updateDoc(reqRef, updates);
}

/**
 * Mark as billed (invoice created in Utilmate)
 */
export async function markServiceRequestAsBilled(
  requestId: string,
  billedBy: string,
  billedByName: string
): Promise<void> {
  const reqRef = doc(db, COLLECTION, requestId);
  const snap = await getDoc(reqRef);
  if (!snap.exists()) throw new Error('Service request not found');

  const current = snap.data() as ServiceRequest;
  const now = new Date().toISOString();

  const entry = makeActivity(
    current.stage,
    billedByName || billedBy,
    'Marked as Billed',
    'Invoice created in external billing system'
  );

  await updateDoc(reqRef, {
    billingStatus: 'billed',
    billedAt: now,
    billedBy,
    billedByName,
    activityLog: [...(current.activityLog || []), entry],
    updatedAt: now,
  });
}

/**
 * Mark as paid
 */
export async function markServiceRequestAsPaid(
  requestId: string,
  paidBy: string,
  paidByName: string
): Promise<void> {
  const reqRef = doc(db, COLLECTION, requestId);
  const snap = await getDoc(reqRef);
  if (!snap.exists()) throw new Error('Service request not found');

  const current = snap.data() as ServiceRequest;
  const now = new Date().toISOString();

  const entry = makeActivity(
    current.stage,
    paidByName || paidBy,
    'Marked as Paid',
    'Payment received'
  );

  await updateDoc(reqRef, {
    billingStatus: 'paid',
    paidAt: now,
    paidBy,
    paidByName,
    activityLog: [...(current.activityLog || []), entry],
    updatedAt: now,
  });
}

/**
 * Mark as bad debtor — office override for invoiced requests not being paid.
 * Captures a reason so the audit trail explains why it was flagged.
 */
export async function markServiceRequestAsBadDebtor(
  requestId: string,
  flaggedBy: string,
  flaggedByName: string,
  reason: string
): Promise<void> {
  const reqRef = doc(db, COLLECTION, requestId);
  const snap = await getDoc(reqRef);
  if (!snap.exists()) throw new Error('Service request not found');

  const current = snap.data() as ServiceRequest;
  const now = new Date().toISOString();

  const entry = makeActivity(
    current.stage,
    flaggedByName || flaggedBy,
    'Flagged as Bad Debtor',
    reason || 'Invoice not being paid'
  );

  await updateDoc(reqRef, {
    billingStatus: 'bad_debtor',
    badDebtorReason: reason || 'Invoice not being paid',
    badDebtorFlaggedAt: now,
    badDebtorFlaggedBy: flaggedBy,
    badDebtorFlaggedByName: flaggedByName,
    activityLog: [...(current.activityLog || []), entry],
    updatedAt: now,
  });
}

/**
 * Restore a bad debtor back to 'billed' — used if payment finally arrives
 * or the office wants to keep chasing.
 */
export async function restoreServiceRequestFromBadDebtor(
  requestId: string,
  restoredBy: string,
  restoredByName: string
): Promise<void> {
  const reqRef = doc(db, COLLECTION, requestId);
  const snap = await getDoc(reqRef);
  if (!snap.exists()) throw new Error('Service request not found');

  const current = snap.data() as ServiceRequest;
  const now = new Date().toISOString();

  const entry = makeActivity(
    current.stage,
    restoredByName || restoredBy,
    'Restored from Bad Debtor',
    'Back to Awaiting Payment'
  );

  await updateDoc(reqRef, {
    billingStatus: 'billed',
    badDebtorReason: null,
    badDebtorFlaggedAt: null,
    badDebtorFlaggedBy: null,
    badDebtorFlaggedByName: null,
    activityLog: [...(current.activityLog || []), entry],
    updatedAt: now,
  });
}

/**
 * Get all service requests for a customer (for history views)
 */
export async function getServiceRequestsForCustomer(
  customerId: string
): Promise<ServiceRequest[]> {
  if (!customerId) return [];
  const q = query(
    collection(db, COLLECTION),
    where('tenantId', 'in', tenantIdReadUnion(getTenantId())),
    where('customerId', '==', customerId),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as ServiceRequest) }));
}

/**
 * Get all service requests linked to a specific customer service
 */
export async function getServiceRequestsForService(
  serviceId: string
): Promise<ServiceRequest[]> {
  if (!serviceId) return [];
  const q = query(
    collection(db, COLLECTION),
    where('tenantId', 'in', tenantIdReadUnion(getTenantId())),
    where('serviceId', '==', serviceId),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as ServiceRequest) }));
}

// ============================================================================
// NBN UPGRADE — specialised helpers
// ============================================================================

/**
 * Update NBN-specific data (target tech, speed tier, order ref, appointment, hold).
 * Merges into existing `nbnUpgrade` object — only set fields are updated.
 */
export async function updateNBNUpgradeData(
  requestId: string,
  author: string,
  updates: {
    targetTechnology?: string;
    targetSpeedTier?: string;
    nbnOrderRef?: string;
    nbnAppointmentDate?: string;
    nbnAppointmentWindow?: string;
    nbnCompletedAt?: string;
    heldEta?: string;
    heldNotes?: string;
    heldAt?: string;
  },
  noteDetails?: string,
): Promise<void> {
  const reqRef = doc(db, COLLECTION, requestId);
  const snap = await getDoc(reqRef);
  if (!snap.exists()) throw new Error('Service request not found');

  const current = snap.data() as ServiceRequest;
  const now = new Date().toISOString();

  const merged = {
    ...(current.nbnUpgrade || {}),
    ...Object.fromEntries(Object.entries(updates).filter(([, v]) => v !== undefined && v !== '')),
  };

  const entry = makeActivity(
    current.stage,
    author,
    'NBN details updated',
    noteDetails ||
      [
        updates.targetTechnology ? `Target: ${updates.targetTechnology}` : '',
        updates.targetSpeedTier ? `Speed: ${updates.targetSpeedTier}` : '',
        updates.nbnOrderRef ? `NBN Ref: ${updates.nbnOrderRef}` : '',
        updates.nbnAppointmentDate
          ? `NBN Appt: ${updates.nbnAppointmentDate}${updates.nbnAppointmentWindow ? ` (${updates.nbnAppointmentWindow})` : ''}`
          : '',
        updates.heldEta ? `Hold ETA: ${updates.heldEta}` : '',
      ]
        .filter(Boolean)
        .join(' · ') || 'NBN details updated',
  );

  await updateDoc(reqRef, {
    nbnUpgrade: merged,
    activityLog: [...(current.activityLog || []), entry],
    updatedAt: now,
  });
}

/**
 * Advance to NBN-03-Appt-Confirmed and capture the booked NBN appointment
 * date/time in one atomic write. Used when the dropdown target stage is
 * Appt Confirmed — the UI prompts for the date/window first.
 */
export async function advanceToApptConfirmed(
  requestId: string,
  author: string,
  appointment: { date: string; window?: string },
): Promise<void> {
  const reqRef = doc(db, COLLECTION, requestId);
  const snap = await getDoc(reqRef);
  if (!snap.exists()) throw new Error('Service request not found');
  const current = snap.data() as ServiceRequest;
  const now = new Date().toISOString();

  const entry = makeActivity(
    'NBN-03-Appt-Confirmed',
    author,
    'NBN appointment confirmed',
    `Booked for ${appointment.date}${appointment.window ? ` (${appointment.window})` : ''}`,
  );

  await updateDoc(reqRef, {
    stage: 'NBN-03-Appt-Confirmed',
    nbnUpgrade: {
      ...(current.nbnUpgrade || {}),
      nbnAppointmentDate: appointment.date,
      ...(appointment.window ? { nbnAppointmentWindow: appointment.window } : {}),
    },
    activityLog: [...(current.activityLog || []), entry],
    updatedAt: now,
  });
}

/**
 * Advance to a Held sub-stage and capture the ETA + notes.
 */
export async function advanceToHeld(
  requestId: string,
  author: string,
  heldStage: 'NBN-03a-Held-NBN-Civil' | 'NBN-03b-Held-Customer',
  hold: { eta?: string; notes?: string },
): Promise<void> {
  const reqRef = doc(db, COLLECTION, requestId);
  const snap = await getDoc(reqRef);
  if (!snap.exists()) throw new Error('Service request not found');
  const current = snap.data() as ServiceRequest;
  const now = new Date().toISOString();

  const label =
    heldStage === 'NBN-03a-Held-NBN-Civil'
      ? 'NBN civil / additional work'
      : 'customer (GPO / access / decision)';

  const entry = makeActivity(
    heldStage,
    author,
    'Held',
    `Awaiting ${label}${hold.eta ? ` — ETA ${hold.eta}` : ''}${hold.notes ? ` — ${hold.notes}` : ''}`,
  );

  await updateDoc(reqRef, {
    stage: heldStage,
    nbnUpgrade: {
      ...(current.nbnUpgrade || {}),
      heldAt: now,
      ...(hold.eta ? { heldEta: hold.eta } : {}),
      ...(hold.notes ? { heldNotes: hold.notes } : {}),
    },
    activityLog: [...(current.activityLog || []), entry],
    updatedAt: now,
  });
}

// ============================================================================
// JOB LINKING — Create Job from Service Request
// ============================================================================

/**
 * Create a job linked to a service request.
 *
 * Used when a service request grows beyond remote work and needs a tech on-site —
 * most commonly for the "Our Install Scheduled" stage of an NBN Upgrade, but
 * available for any request type. The job carries full customer/address/service
 * context, and the service request records the linked job ID in `linkedJobIds`.
 *
 * Lands as Unscheduled unless a date is supplied.
 */
export async function quickRaiseJobFromServiceRequest(
  requestId: string,
  userId: string,
  userName: string,
  details: {
    jobType: 'Install' | 'Fault' | 'Pre-Site' | 'Task';
    reason: string;
    estimatedDuration: number;
    preferredTechIds?: string[];
    preferredTechNames?: string[];
  },
): Promise<string> {
  const reqRef = doc(db, COLLECTION, requestId);
  const reqSnap = await getDoc(reqRef);
  if (!reqSnap.exists()) throw new Error(`Service request ${requestId} not found`);

  const request = reqSnap.data() as ServiceRequest;
  const now = new Date().toISOString();
  const tenantId = (request as { tenantId?: string }).tenantId ?? getTenantId();

  // Pull customer address (if available) from the customer doc
  let customerAddress = '';
  let customerCity = '';
  let customerState = '';
  let customerPostcode = '';
  let criticalNotes = '';
  let contactName = request.customerName;
  let contactPhone = '';
  let contactEmail = '';

  try {
    const customerSnap = await getDoc(doc(db, 'customers', request.customerId));
    if (customerSnap.exists()) {
      const customer = customerSnap.data() as {
        address?: string;
        city?: string;
        state?: string;
        postcode?: string;
        zipCode?: string;
        accessInstructions?: string;
        name?: string;
        phone?: string;
        email?: string;
      };
      customerAddress = customer.address || '';
      customerCity = customer.city || '';
      // State + postcode were dropped here, so the job the SR raised showed a
      // street number and (at best) a suburb on every read surface.
      customerState = customer.state || '';
      customerPostcode = customer.postcode || customer.zipCode || '';
      criticalNotes = customer.accessInstructions || '';
      contactName = customer.name || request.customerName;
      contactPhone = customer.phone || '';
      contactEmail = customer.email || '';
    }
  } catch {
    // Non-blocking — proceed without customer address
  }

  // Geocode the FULL address — a street line on its own resolves to whichever
  // matching street the geocoder likes best, which is rarely the right town.
  const fullSiteAddress = composeSiteAddress({
    address: customerAddress,
    city: customerCity,
    state: customerState,
    postcode: customerPostcode,
  });
  const geocoded = fullSiteAddress
    ? await geocodeAddress(fullSiteAddress).catch(() => null)
    : null;
  const coords = geocoded
    ? { lat: geocoded.lat, lng: geocoded.lng }
    : { lat: JOB_FALLBACK_COORDS.lat, lng: JOB_FALLBACK_COORDS.lng };

  const hasTechs = (details.preferredTechIds?.length ?? 0) > 0;

  const newJob: Record<string, unknown> = {
    jobType: 'job',
    client: request.customerName,
    type: details.jobType,
    status: 'Unscheduled',
    tenantId,

    // Location
    address: customerAddress,
    city: customerCity,
    state: customerState,
    postcode: customerPostcode,
    loc: customerCity,
    lat: coords.lat,
    lng: coords.lng,
    ...(geocoded
      ? { geocodedAt: now, geocodedAddress: geocoded.formattedAddress || fullSiteAddress }
      : {}),

    // Scheduling — unscheduled, ops drags onto calendar
    date: '',
    time: '09:00',
    duration: details.estimatedDuration,

    // Tech preference (as guidance when unscheduled)
    techIds: [],
    techId: '',
    ...(hasTechs
      ? {
          schedulingGuidance: {
            preferredTechIds: details.preferredTechIds,
            preferredTechNames: details.preferredTechNames || [],
          },
        }
      : {}),

    // Customer link
    customerId: request.customerId,
    contactName,
    contact: contactPhone,
    contactEmail,
    criticalNotes,

    // Service context
    services: request.serviceType ? [request.serviceType] : [],
    ...(request.serviceId ? { serviceId: request.serviceId } : {}),
    ...(request.serviceType ? { serviceType: request.serviceType } : {}),
    ...(request.servicePlanName ? { servicePlanName: request.servicePlanName } : {}),

    // Service Request link (bidirectional)
    serviceRequestId: requestId,
    serviceRequestType: request.requestType,

    // Notes — capture the reason + surrounding context
    notes: [
      `LINKED SERVICE REQUEST — ${details.jobType.toUpperCase()}`,
      details.reason,
      '',
      `Request: ${request.title}`,
      request.description ? `Detail: ${request.description}` : '',
      request.nbnUpgrade?.nbnOrderRef ? `NBN Order: ${request.nbnUpgrade.nbnOrderRef}` : '',
      request.nbnUpgrade?.targetTechnology
        ? `Target Tech: ${request.nbnUpgrade.targetTechnology}${request.nbnUpgrade.targetSpeedTier ? ` @ ${request.nbnUpgrade.targetSpeedTier}` : ''}`
        : '',
    ]
      .filter(Boolean)
      .join('\n'),

    // Offline copy of the field briefing. The tech card reads the LIVE request
    // first; this only has to survive a tech opening the job with no signal.
    ...(request.techBriefing ? { techBriefing: request.techBriefing } : {}),

    // Metadata
    quickRaised: true,
    quickRaisedReason: details.reason,
    quickRaisedAt: now,
    quickRaisedBy: userId,
    quickRaisedByName: userName,
    quickRaisedSource: 'service_request',

    // Defaults
    checklist: [],
    sos: false,
    deleted: false,
    createdAt: now,
    updatedAt: now,
  };

  // Auto-attach checklist template (non-blocking)
  try {
    const checkpoints = await getCheckpointsForNewJob(
      tenantId,
      request.serviceType || '',
      details.jobType,
    );
    if (checkpoints) newJob.checkpoints = checkpoints;
  } catch {
    // Non-blocking
  }

  // Strip undefined DEEP, not top-level. `techBriefing` and `checkpoints` are
  // nested structures copied in wholesale, and a top-level loop never looks
  // inside them — the trap that killed return-visit creation for a fortnight
  // (see job-side-effects.service.ts).
  const cleanedJob = stripUndefinedDeep(newJob);

  const jobRef = await addDoc(collection(db, 'jobs'), cleanedJob);

  // Link back to the service request + append activity
  const activity = makeActivity(
    request.stage,
    userName,
    `Created ${details.jobType} job`,
    details.reason,
  );

  await updateDoc(reqRef, {
    linkedJobIds: arrayUnion(jobRef.id),
    activityLog: [...(request.activityLog || []), activity],
    updatedAt: now,
  });

  return jobRef.id;
}

// ============================================================================
// LINE ITEMS — products / services / charges on a service request
// ============================================================================

/**
 * Default billing type inferred from the line item category. Callers can
 * override explicitly, but this covers the common cases.
 */
function defaultBillingTypeForCategory(
  category: ServiceRequestLineItemCategory,
): 'one-time' | 'recurring' {
  return category === 'service_plan' ? 'recurring' : 'one-time';
}

/**
 * Recompute one-time / recurring / ACV totals from a line item list.
 */
function buildTotals(items: ServiceRequestLineItem[]) {
  return computeLineItemTotals(items);
}

/**
 * Replace the entire line items array in one write. Use for bulk edits
 * (e.g. applying a template) — prefer add/update/remove for single-row
 * mutations so the activity log stays granular.
 */
export async function setLineItems(
  requestId: string,
  items: ServiceRequestLineItem[],
  author: string,
  note?: string,
): Promise<void> {
  const reqRef = doc(db, COLLECTION, requestId);
  const snap = await getDoc(reqRef);
  if (!snap.exists()) throw new Error('Service request not found');
  const current = snap.data() as ServiceRequest;

  const totals = buildTotals(items);
  const entry = makeActivity(
    current.stage,
    author,
    'Line items updated',
    note ||
      `${items.length} item${items.length === 1 ? '' : 's'} · $${totals.oneTimeTotal.toFixed(2)} one-off · $${totals.monthlyRecurringTotal.toFixed(2)}/mo`,
    'line_items',
  );

  await updateDoc(reqRef, stripUndefined({
    lineItems: items,
    oneTimeTotal: totals.oneTimeTotal,
    monthlyRecurringTotal: totals.monthlyRecurringTotal,
    annualContractValue: totals.annualContractValue,
    activityLog: [...(current.activityLog || []), entry],
    updatedAt: new Date().toISOString(),
  }));
}

/**
 * Add a single line item to the request. Returns the new item's ID.
 */
export async function addLineItem(
  requestId: string,
  author: string,
  input: Omit<ServiceRequestLineItem, 'id' | 'addedAt' | 'addedBy' | 'billingType'> & {
    billingType?: 'one-time' | 'recurring';
  },
): Promise<string> {
  const reqRef = doc(db, COLLECTION, requestId);
  const snap = await getDoc(reqRef);
  if (!snap.exists()) throw new Error('Service request not found');
  const current = snap.data() as ServiceRequest;

  const now = new Date().toISOString();
  const id = crypto.randomUUID();
  const billingType = input.billingType ?? defaultBillingTypeForCategory(input.category);

  const item: ServiceRequestLineItem = {
    id,
    category: input.category,
    name: input.name.trim(),
    description: input.description?.trim() || undefined,
    quantity: Number.isFinite(input.quantity) && input.quantity > 0 ? input.quantity : 1,
    unitPrice: Number.isFinite(input.unitPrice) ? input.unitPrice : 0,
    billingType,
    notes: input.notes?.trim() || undefined,
    partsCatalogId: input.partsCatalogId,
    servicePlanId: input.servicePlanId,
    addedAt: now,
    addedBy: author,
  };

  const items = [...(current.lineItems || []), item];
  const totals = buildTotals(items);

  const sub = item.quantity * item.unitPrice;
  const entry = makeActivity(
    current.stage,
    author,
    'Line item added',
    `${item.name} · ${item.quantity} × $${item.unitPrice.toFixed(2)} = $${sub.toFixed(2)}${billingType === 'recurring' ? '/mo' : ''}`,
    'line_items',
  );

  await updateDoc(reqRef, stripUndefined({
    lineItems: items,
    oneTimeTotal: totals.oneTimeTotal,
    monthlyRecurringTotal: totals.monthlyRecurringTotal,
    annualContractValue: totals.annualContractValue,
    activityLog: [...(current.activityLog || []), entry],
    updatedAt: now,
  }));

  return id;
}

/**
 * Update a single line item by ID. Silently no-ops if the item isn't found.
 */
export async function updateLineItem(
  requestId: string,
  itemId: string,
  author: string,
  patch: Partial<Omit<ServiceRequestLineItem, 'id' | 'addedAt' | 'addedBy'>>,
): Promise<void> {
  const reqRef = doc(db, COLLECTION, requestId);
  const snap = await getDoc(reqRef);
  if (!snap.exists()) throw new Error('Service request not found');
  const current = snap.data() as ServiceRequest;

  const items = (current.lineItems || []).map((it) =>
    it.id === itemId ? { ...it, ...patch } : it,
  );
  const found = items.some((it) => it.id === itemId);
  if (!found) return;

  const totals = buildTotals(items);
  const entry = makeActivity(
    current.stage,
    author,
    'Line item updated',
    `${patch.name || 'Item'} updated`,
    'line_items',
  );

  await updateDoc(reqRef, stripUndefined({
    lineItems: items,
    oneTimeTotal: totals.oneTimeTotal,
    monthlyRecurringTotal: totals.monthlyRecurringTotal,
    annualContractValue: totals.annualContractValue,
    activityLog: [...(current.activityLog || []), entry],
    updatedAt: new Date().toISOString(),
  }));
}

/**
 * Remove a single line item by ID.
 */
export async function removeLineItem(
  requestId: string,
  itemId: string,
  author: string,
): Promise<void> {
  const reqRef = doc(db, COLLECTION, requestId);
  const snap = await getDoc(reqRef);
  if (!snap.exists()) throw new Error('Service request not found');
  const current = snap.data() as ServiceRequest;

  const target = (current.lineItems || []).find((it) => it.id === itemId);
  if (!target) return;

  const items = (current.lineItems || []).filter((it) => it.id !== itemId);
  const totals = buildTotals(items);
  const entry = makeActivity(
    current.stage,
    author,
    'Line item removed',
    target.name,
    'line_items',
  );

  await updateDoc(reqRef, stripUndefined({
    lineItems: items,
    oneTimeTotal: totals.oneTimeTotal,
    monthlyRecurringTotal: totals.monthlyRecurringTotal,
    annualContractValue: totals.annualContractValue,
    activityLog: [...(current.activityLog || []), entry],
    updatedAt: new Date().toISOString(),
  }));
}

// ============================================================================
// VARIATIONS — alternative bundles the customer is choosing between
// ============================================================================

/**
 * Create a new variation. If `copyFromLineItems` is true, clones the current
 * lineItems as the variation's starting point (useful for "duplicate and
 * tweak" flows).
 */
export async function addVariation(
  requestId: string,
  author: string,
  input: {
    name: string;
    description?: string;
    items?: ServiceRequestLineItem[];
    copyFromLineItems?: boolean;
  },
): Promise<string> {
  const reqRef = doc(db, COLLECTION, requestId);
  const snap = await getDoc(reqRef);
  if (!snap.exists()) throw new Error('Service request not found');
  const current = snap.data() as ServiceRequest;

  const now = new Date().toISOString();
  const id = crypto.randomUUID();
  const seed: ServiceRequestLineItem[] =
    input.items ||
    (input.copyFromLineItems ? (current.lineItems || []).map((it) => ({ ...it, id: crypto.randomUUID() })) : []);

  const variation: ServiceRequestVariation = {
    id,
    name: input.name.trim() || `Option ${(current.variations?.length || 0) + 1}`,
    description: input.description?.trim() || undefined,
    items: seed,
    createdAt: now,
    createdBy: author,
  };

  const variations = [...(current.variations || []), variation];
  const entry = makeActivity(
    current.stage,
    author,
    'Variation added',
    variation.name,
    'variation',
  );

  await updateDoc(reqRef, stripUndefined({
    variations,
    activityLog: [...(current.activityLog || []), entry],
    updatedAt: now,
  }));

  return id;
}

/**
 * Rename / describe / replace items on a variation.
 */
export async function updateVariation(
  requestId: string,
  variationId: string,
  author: string,
  patch: Partial<Pick<ServiceRequestVariation, 'name' | 'description' | 'items'>>,
): Promise<void> {
  const reqRef = doc(db, COLLECTION, requestId);
  const snap = await getDoc(reqRef);
  if (!snap.exists()) throw new Error('Service request not found');
  const current = snap.data() as ServiceRequest;

  const variations = (current.variations || []).map((v) =>
    v.id === variationId ? { ...v, ...patch } : v,
  );
  const isActive = current.activeVariationId === variationId;

  const updates: Record<string, unknown> = {
    variations,
    updatedAt: new Date().toISOString(),
  };

  // If the active variation's items changed, mirror them into lineItems + totals
  if (isActive && patch.items) {
    const totals = buildTotals(patch.items);
    updates.lineItems = patch.items;
    updates.oneTimeTotal = totals.oneTimeTotal;
    updates.monthlyRecurringTotal = totals.monthlyRecurringTotal;
    updates.annualContractValue = totals.annualContractValue;
  }

  const entry = makeActivity(
    current.stage,
    author,
    'Variation updated',
    patch.name || variations.find((v) => v.id === variationId)?.name || 'Variation',
    'variation',
  );
  updates.activityLog = [...(current.activityLog || []), entry];

  await updateDoc(reqRef, stripUndefined(updates));
}

export async function removeVariation(
  requestId: string,
  variationId: string,
  author: string,
): Promise<void> {
  const reqRef = doc(db, COLLECTION, requestId);
  const snap = await getDoc(reqRef);
  if (!snap.exists()) throw new Error('Service request not found');
  const current = snap.data() as ServiceRequest;

  const target = (current.variations || []).find((v) => v.id === variationId);
  if (!target) return;

  const variations = (current.variations || []).filter((v) => v.id !== variationId);

  const updates: Record<string, unknown> = {
    variations,
    updatedAt: new Date().toISOString(),
  };

  // If we removed the active variation, clear the activeVariationId pointer
  if (current.activeVariationId === variationId) {
    updates.activeVariationId = null;
  }

  const entry = makeActivity(
    current.stage,
    author,
    'Variation removed',
    target.name,
    'variation',
  );
  updates.activityLog = [...(current.activityLog || []), entry];

  await updateDoc(reqRef, stripUndefined(updates));
}

/**
 * Pick a variation — mirrors its items into the parent `lineItems` array
 * and marks it as the active one. Other variations have their `isActive`
 * flag cleared.
 */
export async function selectVariation(
  requestId: string,
  variationId: string,
  author: string,
): Promise<void> {
  const reqRef = doc(db, COLLECTION, requestId);
  const snap = await getDoc(reqRef);
  if (!snap.exists()) throw new Error('Service request not found');
  const current = snap.data() as ServiceRequest;

  const target = (current.variations || []).find((v) => v.id === variationId);
  if (!target) throw new Error('Variation not found');

  const now = new Date().toISOString();
  const variations = (current.variations || []).map((v) => ({
    ...v,
    isActive: v.id === variationId,
    ...(v.id === variationId ? { selectedAt: now, selectedBy: author } : {}),
  }));

  // Fresh IDs for the mirrored line items so editing lineItems doesn't mutate
  // the stored variation items by reference.
  const lineItems: ServiceRequestLineItem[] = target.items.map((it) => ({
    ...it,
    id: crypto.randomUUID(),
  }));

  const totals = buildTotals(lineItems);

  const entry = makeActivity(
    current.stage,
    author,
    `Variation selected`,
    `${target.name} · $${totals.oneTimeTotal.toFixed(2)} one-off · $${totals.monthlyRecurringTotal.toFixed(2)}/mo`,
    'variation',
  );

  await updateDoc(reqRef, stripUndefined({
    variations,
    activeVariationId: variationId,
    lineItems,
    oneTimeTotal: totals.oneTimeTotal,
    monthlyRecurringTotal: totals.monthlyRecurringTotal,
    annualContractValue: totals.annualContractValue,
    activityLog: [...(current.activityLog || []), entry],
    updatedAt: now,
  }));
}

// ============================================================================
// CURRENT SERVICE SNAPSHOT
// ============================================================================

/**
 * Capture a snapshot of the customer's current service at the time of the
 * request — used to show "before vs after" on NBN upgrades / plan changes.
 */
export async function setCurrentServiceSnapshot(
  requestId: string,
  author: string,
  snapshot: Omit<ServiceRequestCurrentSnapshot, 'capturedAt'>,
): Promise<void> {
  const reqRef = doc(db, COLLECTION, requestId);
  const snap = await getDoc(reqRef);
  if (!snap.exists()) throw new Error('Service request not found');
  const current = snap.data() as ServiceRequest;

  const now = new Date().toISOString();
  const fullSnapshot: ServiceRequestCurrentSnapshot = {
    ...snapshot,
    capturedAt: now,
  };

  const entry = makeActivity(
    current.stage,
    author,
    'Current service snapshot captured',
    [snapshot.planName, snapshot.speedTier, snapshot.technology].filter(Boolean).join(' · ') ||
      'Snapshot recorded',
    'line_items',
  );

  await updateDoc(reqRef, stripUndefined({
    currentServiceSnapshot: fullSnapshot,
    activityLog: [...(current.activityLog || []), entry],
    updatedAt: now,
  }));
}

// ============================================================================
// STAGE OVERRIDE — explicit non-sequential stage change with audit
// ============================================================================

/**
 * Override the stage to ANY value (including past stages, cross-pipeline
 * jumps, etc.) with a required reason. Writes to both `activityLog` and
 * `stageOverrides` so the audit is easy to surface separately.
 */
export async function overrideStage(
  requestId: string,
  targetStage: ServiceRequestStage,
  reason: string,
  author: string,
  authorId?: string,
): Promise<void> {
  if (!reason.trim()) {
    throw new Error('A reason is required to override the stage');
  }
  const reqRef = doc(db, COLLECTION, requestId);
  const snap = await getDoc(reqRef);
  if (!snap.exists()) throw new Error('Service request not found');
  const current = snap.data() as ServiceRequest;

  const now = new Date().toISOString();
  const fromStage = current.stage;

  if (fromStage === targetStage) {
    throw new Error('Target stage is the same as the current stage');
  }

  const override: ServiceRequestStageOverride = {
    id: crypto.randomUUID(),
    timestamp: now,
    fromStage,
    toStage: targetStage,
    reason: reason.trim(),
    overriddenBy: author,
    overriddenById: authorId,
  };

  const fromLabel = SERVICE_REQUEST_STAGE_LABELS[fromStage] || fromStage;
  const toLabel = SERVICE_REQUEST_STAGE_LABELS[targetStage] || targetStage;

  const entry = makeActivity(
    targetStage,
    author,
    `Stage overridden: ${fromLabel} → ${toLabel}`,
    reason.trim(),
    'stage_override',
  );

  // If overriding back from a Completed stage, clear the archived flag so the
  // request comes back into the active pipeline.
  const updates: Record<string, unknown> = {
    stage: targetStage,
    stageOverrides: [...(current.stageOverrides || []), override],
    activityLog: [...(current.activityLog || []), entry],
    updatedAt: now,
  };

  const wasCompleted =
    fromStage === '05-Completed' || fromStage === 'NBN-05-Completed';
  const movingBackFromCompleted = wasCompleted && targetStage !== fromStage;
  if (movingBackFromCompleted) {
    updates.archived = false;
    updates.archivedAt = null;
  }

  await updateDoc(reqRef, stripUndefined(updates));
}
